import { useProfile } from "@/hooks/useProfile";
import { useStayPlans, type StayPlan } from "@/hooks/useStayPlans";
import { useStayPlanContracts } from "@/hooks/useStayPlanContracts";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { useStayDailyRates, type StayDailyRates } from "@/hooks/useStayDailyRates";

export interface ReservationConfiguredPlan {
  planId: string;
  name: string;
  price: number;
  kind: StayPlan["kind"];
  includedDays: number | null;
  usageRule: StayPlan["usageRule"];
  usageLimit: number | null;
}

export interface ReservationAssignedPlan {
  contractId: string;
  planId: string;
  name: string;
  price: number;
  remainingDays: number | null;
  startsOn: string;
  status: string;
  origin: "existing";
  kind: StayPlan["kind"];
}

export function normalizeReservationConfiguredPlan(plan: StayPlan): ReservationConfiguredPlan {
  return {
    planId: plan.id,
    name: plan.name,
    price: plan.price,
    kind: plan.kind,
    includedDays: plan.includedDays,
    usageRule: plan.usageRule,
    usageLimit: plan.usageLimit,
  };
}

export function filterReservationAssignedPlans(rows: Array<ReservationAssignedPlan>, checkInDate: string) {
  return rows.filter((row) => row.status === "active" && row.startsOn <= checkInDate && (row.remainingDays === null || row.remainingDays > 0));
}

export function useReservationBillingOptions({
  module,
  petId,
  checkInDate,
}: {
  module: StayPlanModule;
  petId: string | null;
  checkInDate: string;
}) {
  const { data: profile } = useProfile();
  const plans = useStayPlans(module);
  const dailyRates = useStayDailyRates();
  const clinicId = profile?.current_clinic_id ?? null;
  const contracts = useStayPlanContracts(module, clinicId, petId ? [petId] : []);
  const assignedRows: ReservationAssignedPlan[] = (contracts.data ?? []).map((contract) => ({
    contractId: contract.id,
    planId: contract.planId,
    name: contract.planName,
    price: contract.price,
    remainingDays: contract.remainingDays,
    startsOn: contract.startsOn,
    status: contract.status,
    origin: "existing" as const,
    kind: contract.kind,
  }));

  return {
    configuredPlans: (plans.data ?? []).filter((plan) => plan.isActive).map(normalizeReservationConfiguredPlan),
    assignedContracts: filterReservationAssignedPlans(assignedRows, checkInDate),
    dailyRates: dailyRates.data ?? null as StayDailyRates | null,
    isLoading: plans.isLoading || dailyRates.isLoading || contracts.isLoading,
    isFetching: contracts.isFetching,
    isError: plans.isError || dailyRates.isError || contracts.isError,
  };
}
